import { useState } from 'react';
import { alpha, Box, ClickAwayListener, Popper, useTheme } from '@mui/material';
import { SGTooltip } from 'components/core';
import { GroupColorPicker } from './GroupColorPicker';

interface GroupColorButtonProps {
  color: string;
  onChange: (color: string) => void;
}

/** The group's color dot; clicking opens a swatch/hex popover to recolor it. */
export const GroupColorButton = ({
  color,
  onChange,
}: GroupColorButtonProps) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = anchorEl != null;

  return (
    <>
      <SGTooltip title="Change color" placement="top">
        <Box
          data-ph-capture-attribute-element="canvas-group-color-open"
          role="button"
          aria-label="Change group color"
          onClick={(e) => setAnchorEl(open ? null : e.currentTarget)}
          sx={{
            width: 14,
            height: 14,
            borderRadius: '50%',
            backgroundColor: color,
            cursor: 'pointer',
            flexShrink: 0,
            boxShadow: `0 0 0 2px ${alpha(color, 0.25)}`,
            transition: 'transform 120ms',
            '&:hover': { transform: 'scale(1.12)' },
          }}
        />
      </SGTooltip>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-start"
        style={{ zIndex: theme.zIndex.modal + 1 }}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Box
            sx={{
              mt: 2,
              p: 4,
              width: 220,
              backgroundColor: theme.palette.background.paper,
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              boxShadow: theme.palette.shadows.paperBoxShadow,
            }}
          >
            <GroupColorPicker
              value={color}
              onChange={onChange}
              paletteInFront
            />
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  );
};

export default GroupColorButton;
